import type { ContentType } from "@brookbush/database";

import * as HoverCard from "@radix-ui/react-hover-card";
import { clsx } from "clsx";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";

import ChatContentPreview from "~/components/Chat/ChatContentPreview";
import { contentTypeMap } from "~/components/ContentCard";
import Skeleton from "~/components/Skeleton";
import useContentPreview from "~/hooks/useContentPreview";

interface ChatContentLinkProps {
	type: ContentType;
	slug: string;
	href: string;
	label: string;
}

const ChatContentLink = ({ type, slug, href, label }: ChatContentLinkProps) => {
	const reduceMotion = useReducedMotion();
	const [open, setOpen] = useState(false);
	const [armed, setArmed] = useState(false);
	const { content, isLoading } = useContentPreview(type, slug, armed);

	const meta = contentTypeMap[type];
	const Icon = meta.icon;

	const handleOpenChange = (next: boolean) => {
		if (next) setArmed(true);
		setOpen(next);
	};

	return (
		<HoverCard.Root
			openDelay={120}
			closeDelay={80}
			open={open}
			onOpenChange={handleOpenChange}
		>
			<HoverCard.Trigger asChild>
				<Link
					href={href}
					className={clsx(
						"font-medium no-underline hover:underline",
						meta.className,
					)}
				>
					<Icon className="mr-1 inline size-3.5 align-[-0.125em]" />
					{label}
				</Link>
			</HoverCard.Trigger>
			<HoverCard.Portal forceMount>
				<AnimatePresence>
					{open && (
						<HoverCard.Content
							asChild
							forceMount
							side="top"
							align="start"
							sideOffset={6}
							collisionPadding={12}
						>
							<motion.div
								initial={
									reduceMotion ? false : { opacity: 0, y: 4, scale: 0.97 }
								}
								animate={{ opacity: 1, y: 0, scale: 1 }}
								exit={
									reduceMotion
										? { opacity: 0 }
										: { opacity: 0, y: 4, scale: 0.97 }
								}
								transition={{ duration: 0.15, ease: "easeOut" }}
								className="z-50 rounded-xl border bg-white p-2.5 shadow-lg dark:bg-slate-800"
							>
								{content ? (
									<ChatContentPreview content={content} />
								) : isLoading ? (
									<div className="flex w-64 gap-2.5">
										<Skeleton className="aspect-square h-14 shrink-0 rounded-lg" />
										<div className="flex flex-1 flex-col justify-center gap-1.5">
											<Skeleton className="h-3 w-16 rounded" />
											<Skeleton className="h-3.5 w-full rounded" />
										</div>
									</div>
								) : (
									<div className="flex w-64 items-center gap-2">
										<span className={clsx("shrink-0", meta.className)}>
											<Icon className="size-5" />
										</span>
										<span className="line-clamp-2 font-medium text-sm">
											{label}
										</span>
									</div>
								)}
							</motion.div>
						</HoverCard.Content>
					)}
				</AnimatePresence>
			</HoverCard.Portal>
		</HoverCard.Root>
	);
};

export default ChatContentLink;
